// Interest rate conversion utilities and credit-modality benchmarks.

export type RatePeriod = "monthly" | "annual";

export function annualToMonthly(annualPercent: number): number {
  const i = annualPercent / 100;
  return (Math.pow(1 + i, 1 / 12) - 1) * 100;
}

export function monthlyToAnnual(monthlyPercent: number): number {
  const i = monthlyPercent / 100;
  return (Math.pow(1 + i, 12) - 1) * 100;
}

export function toMonthlyDecimal(ratePercent: number, period: RatePeriod): number {
  if (period === "monthly") return ratePercent / 100;
  return Math.pow(1 + ratePercent / 100, 1 / 12) - 1;
}

export type AmortizationSystem = "PRICE" | "SAC";

export type CreditModality = "imobiliario" | "veiculos" | "consignado" | "pessoal";

export interface ModalityPreset {
  id: CreditModality;
  label: string;
  description: string;
  recommendedSystem: AmortizationSystem;
  typicalAnnualRate: number;
  typicalTermMonths: number;
  maxTermMonths: number;
  lowMaxAnnual: number;
  mediumMaxAnnual: number;
  rateRanges: { low: string; medium: string; high: string };
  notes: string;
}

export const MODALITIES: ModalityPreset[] = [
  {
    id: "imobiliario",
    label: "Imobiliário",
    description: "Casa ou apartamento, prazos longos",
    recommendedSystem: "SAC",
    typicalAnnualRate: 11.5,
    typicalTermMonths: 360,
    maxTermMonths: 420,
    lowMaxAnnual: 8,
    mediumMaxAnnual: 10,
    rateRanges: {
      low: "até 8% a.a. + TR",
      medium: "8% a 10% a.a. + TR",
      high: "acima de 10% a.a. + TR",
    },
    notes:
      "A Tabela SAC predomina por reduzir o saldo devedor mais rápido. Prazo de até 420 meses (35 anos), comum na Caixa. Considere entrada (20%–30%), seguros e cartório.",
  },
  {
    id: "veiculos",
    label: "Veículos",
    description: "Carro, moto ou caminhão",
    recommendedSystem: "PRICE",
    typicalAnnualRate: 24,
    typicalTermMonths: 48,
    maxTermMonths: 72,
    lowMaxAnnual: 18,
    mediumMaxAnnual: 30,
    rateRanges: {
      low: "1% a 1,5% a.m. (12% a 18% a.a.)",
      medium: "1,6% a 2,5% a.m. (19% a 30% a.a.)",
      high: "acima de 2,6% a.m. (acima de 31% a.a.)",
    },
    notes:
      "A Tabela Price é a mais usada por garantir parcelas fixas. Prazo de até 60 meses (5 anos), às vezes 72. Atenção à depreciação do veículo.",
  },
  {
    id: "consignado",
    label: "Consignado",
    description: "Desconto em folha/benefício",
    recommendedSystem: "PRICE",
    typicalAnnualRate: 22,
    typicalTermMonths: 84,
    maxTermMonths: 96,
    lowMaxAnnual: 20,
    mediumMaxAnnual: 27,
    rateRanges: {
      low: "1,2% a 1,6% a.m. (15% a 20% a.a.)",
      medium: "1,7% a 2,2% a.m. (21% a 27% a.a.)",
      high: "acima de 2,3% a.m. (acima de 28% a.a.)",
    },
    notes:
      "Crédito de baixo risco com taxas competitivas. Prazo até 96 meses (servidores), 84 (INSS) ou 60 (privado). Limitado pela margem consignável (35% da renda).",
  },
  {
    id: "pessoal",
    label: "Pessoal",
    description: "Mais flexível",
    recommendedSystem: "PRICE",
    typicalAnnualRate: 45,
    typicalTermMonths: 36,
    maxTermMonths: 60,
    lowMaxAnnual: 36,
    mediumMaxAnnual: 60,
    rateRanges: {
      low: "2% a 3% a.m. (24% a 36% a.a.)",
      medium: "3,1% a 5% a.m. (37% a 60% a.a.)",
      high: "acima de 5,1% a.m. (acima de 61% a.a.)",
    },
    notes:
      "Tabela Price padrão. Prazo geralmente até 48 meses (às vezes 60). Taxas altas pelo maior risco — evite prazos longos.",
  },
];

export type RateQuality = "low" | "medium" | "high";

export function classifyRate(
  ratePercent: number,
  period: RatePeriod,
  modality: ModalityPreset,
): RateQuality {
  const annual = period === "annual" ? ratePercent : monthlyToAnnual(ratePercent);
  if (annual <= modality.lowMaxAnnual) return "low";
  if (annual <= modality.mediumMaxAnnual) return "medium";
  return "high";
}

export const CORRECTION_PRESETS: { id: string; label: string; monthly: number }[] = [
  { id: "none", label: "Nenhuma", monthly: 0 },
  { id: "tr", label: "TR (~0,10% a.m.)", monthly: 0.1 },
  { id: "poupanca", label: "Poupança (~0,50% a.m.)", monthly: 0.5 },
  { id: "ipca", label: "IPCA (~0,40% a.m.)", monthly: 0.4 },
];
